/**
 * Shared predicates / derivations for ``Idea`` entities.
 */

import type { Idea } from "./types";

/**
 * Whether the idea carries internal linked resources -- i.e. related
 * posts, or any entity link (collection / note / calendar event / ...).
 *
 * Used by Creator-facing views (on-site photographer) to strip the
 * internal planning UI when an idea is already enriched. In that case
 * only the concept and external reference URLs matter for the field.
 */
export function hasInternalLinkedResources(idea: Idea): boolean {
  const linkCount = idea.links?.length ?? 0;
  const postCount = idea.posts?.length ?? 0;
  return linkCount > 0 || postCount > 0;
}

/**
 * Whether an idea body text section is empty. Treats missing,
 * whitespace-only and BlockNote's empty document ("[]") as empty.
 * The single home of this rule; do not re-implement it at call sites.
 */
export function isEmptyBodyText(value: string | undefined): boolean {
  return !value || value.trim() === "" || value === "[]";
}

/**
 * The idea's concept, read from its canonical home ``body.concept``
 * (TD-030). Returns null when absent or empty.
 */
export function getIdeaConcept(idea: Pick<Idea, "body">): string | null {
  const concept = idea.body?.concept;
  return concept && !isEmptyBodyText(concept) ? concept : null;
}

